import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type {
  GeneralPosterVariant,
  HazardPosterTheme,
  HazardPosterVariant,
  PosterLine,
  PosterTheme,
  PosterVariant,
} from '../data/posterThemes';

/* A4縦1ページの安全掲示ポスター。
 *
 * PDF生成ライブラリは使わない。HTML + @page size:A4 の印刷CSSで刷る。
 *  - 文字と図がベクターのまま出る（画像化しないので細い法令文が潰れない）
 *  - 印刷ダイアログで A3 に拡大もできる
 *  - 依存ゼロ（Vercel の関数サイズ制限にも触れない）
 *
 * mm 指定が本質なので、この1ファイルだけ Tailwind ではなく素の CSS を使う。
 * 紙の寸法を rem や Tailwind の spacing で表現しても意味がない。
 *
 * 掲示者名（会社名・現場名）は公開フォームからの入力。文字列は必ず JSX の
 * 子要素として渡し、dangerouslySetInnerHTML は使わない（自動エスケープに乗せる）。
 */

const INK = '#0f172a';
const SUB = '#4b5563';
const ACC_D = '#c2410c';
const ACC = '#f97316';
const LINE = '#cbd5e1';

/** CSS の mm → px 換算（CSS 仕様上 1in = 96px 固定なので定数で足りる）。 */
export const A4_WIDTH_PX = (210 / 25.4) * 96;
export const A4_HEIGHT_PX = (297 / 25.4) * 96;

/** 装飾つきテキストを描画する。em はオレンジ強調、b は本文中の太字。 */
const Rich: React.FC<{ line: PosterLine }> = ({ line }) => (
  <>
    {line.map((seg, i) =>
      seg.em ? (
        <em key={i}>{seg.t}</em>
      ) : seg.b ? (
        <b key={i}>{seg.t}</b>
      ) : (
        <React.Fragment key={i}>{seg.t}</React.Fragment>
      ),
    )}
  </>
);

/** 複数行の装飾つきテキスト。改行位置は組版上の意味を持つのでデータ側の行構造に従う。 */
const RichLines: React.FC<{ lines: readonly PosterLine[] }> = ({ lines }) => (
  <>
    {lines.map((line, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        <Rich line={line} />
      </React.Fragment>
    ))}
  </>
);

// ──────────────────────────────────────────────────────────────
// 図解ヒーロー
// 画像生成では日本語文字が必ず破綻するため、図解型は SVG で組む。
// 人物の情緒に頼らず「情報」で伝える1枚として、各テーマに1種ずつ用意する。
// ──────────────────────────────────────────────────────────────

const DIAGRAM_FONT = "'Hiragino Kaku Gothic ProN','Yu Gothic',sans-serif";

/* 図解は 680×520（およそ 1.31:1）で組む。
   A4紙面のヒーロー帯は幅210mm・高さ約125mm＝約1.25:1 なので、
   この比率にしておかないと図が横一列に潰れ、上下が白く抜けて掲示物として弱くなる。 */

/** 熱中症: 症状が体のどこに出るかを示す。装飾（太陽など）は入れない。
 *  頭に出る症状／体に出る症状／脚に出る症状で引き出し先を分ける。
 *  頭痛を腕に、けいれんを腕に引くと図として嘘になるので、部位は必ず合わせる。 */
const HeatSymptomsDiagram: React.FC = () => (
  <svg viewBox="0 0 680 520" preserveAspectRatio="xMidYMid meet" role="img" aria-label="熱中症の症状が体のどこに出るかを示す図">
    {/* 人体（正面・簡略）。白の縁取りで手足を胴から分離させ、拡大しても塊に見えないようにする */}
    <g transform="translate(352,20) scale(1.9)">
      <g fill={INK} stroke="#fff" strokeWidth="1.6">
        {/* 頭・首 */}
        <circle cx="0" cy="27" r="19" />
        <rect x="-7" y="44" width="14" height="9" />
        {/* ヘルメット。頭の上半分を覆う位置に置く（浮かせると帽子に見える） */}
        <path d="M-23 24a23 23 0 0 1 46 0z" />
        <rect x="-28" y="21" width="56" height="5.5" rx="2.75" />
        {/* 胴 */}
        <rect x="-29" y="52" width="58" height="82" rx="15" />
        {/* 腕 */}
        <rect x="-49" y="56" width="17" height="72" rx="8.5" />
        <rect x="32" y="56" width="17" height="72" rx="8.5" />
        {/* 脚 */}
        <rect x="-25" y="138" width="21" height="94" rx="10.5" />
        <rect x="4" y="138" width="21" height="94" rx="10.5" />
      </g>
    </g>

    {/* 引き出し線。左右に振り分け、交差させない。 */}
    <g stroke={ACC_D} strokeWidth="2.6" fill="none">
      <path d="M314 71H256" />
      <path d="M295 170H256" />
      <path d="M302 390H256" />
      <path d="M390 71h58" />
      <path d="M409 200h39" />
    </g>
    <g fill={ACC_D}>
      <circle cx="314" cy="71" r="5.5" />
      <circle cx="295" cy="170" r="5.5" />
      <circle cx="302" cy="390" r="5.5" />
      <circle cx="390" cy="71" r="5.5" />
      <circle cx="409" cy="200" r="5.5" />
    </g>

    <g fontFamily={DIAGRAM_FONT} fontSize="24" fontWeight="700" fill={INK}>
      <g textAnchor="end">
        <text x="248" y="79">めまい・立ちくらみ</text>
        <text x="248" y="178">汗が出ない</text>
        <text x="248" y="398">筋肉のけいれん</text>
      </g>
      <g textAnchor="start">
        <text x="456" y="44">頭痛・吐き気</text>
        <text x="456" y="84">呼びかけへの</text>
        <text x="456" y="112">反応が鈍い</text>
        <text x="456" y="208">体に力が入らない</text>
      </g>
    </g>

    <text
      x="340"
      y="502"
      fontFamily={DIAGRAM_FONT}
      fontSize="21"
      fontWeight="700"
      fill={INK}
      textAnchor="middle"
    >
      ひとつでも当てはまれば、その場で作業をやめる。
    </text>
  </svg>
);

/** 墜落: 高さの閾値で必要な措置が変わることを1枚で示す（2m / 6.75m）。 */
const FallHeightDiagram: React.FC = () => (
  <svg viewBox="0 0 680 520" preserveAspectRatio="xMidYMid meet" role="img" aria-label="高さ2mと6.75mで必要な墜落防止措置が変わることを示す図">
    {/* 高さの帯。上に行くほど濃くする */}
    <rect x="30" y="20" width="620" height="180" fill="#ffedd5" />
    <rect x="30" y="200" width="620" height="160" fill="#fff7ed" />
    <rect x="30" y="360" width="620" height="120" fill="#ffffff" />

    {/* 高さの柱と地面 */}
    <rect x="136" y="20" width="18" height="460" fill={INK} />
    <path d="M30 480H650" stroke={INK} strokeWidth="4" />
    <g stroke={INK} strokeWidth="2" opacity="0.45">
      <path d="M40 480l-10 16M80 480l-10 16M120 480l-10 16M160 480l-10 16M200 480l-10 16M240 480l-10 16" />
    </g>

    {/* 境界 */}
    <g stroke={ACC_D} strokeWidth="3">
      <path d="M154 200h196" />
      <path d="M154 360h196" />
    </g>
    <g fill={ACC_D}>
      <circle cx="145" cy="200" r="8" />
      <circle cx="145" cy="360" r="8" />
    </g>

    <g fontFamily={DIAGRAM_FONT} fill={INK}>
      <g textAnchor="end" fontSize="27" fontWeight="800">
        <text x="126" y="192">6.75 m</text>
        <text x="126" y="352">2 m</text>
      </g>
      <g textAnchor="start">
        <text x="366" y="100" fontSize="29" fontWeight="800">これを超えたら</text>
        <text x="366" y="150" fontSize="29" fontWeight="800" fill={ACC_D}>フルハーネス型に限る</text>
        <text x="366" y="265" fontSize="29" fontWeight="800">作業床・手すり・</text>
        <text x="366" y="305" fontSize="29" fontWeight="800">囲い・覆いが必要</text>
        <text x="356" y="432" fontSize="18" fill={SUB}>低い場所でも、頭から落ちれば同じ。</text>
      </g>
    </g>
  </svg>
);

/** はさまれ: 手を入れる前の4手順（止める・切る・施錠・確認）。 */
const CaughtLotoDiagram: React.FC = () => {
  const steps: Array<{ title: string; a: string; b: string; icon: React.ReactNode }> = [
    {
      title: '止める',
      a: '運転を停止する',
      b: '非常停止も使う',
      icon: (
        <>
          <circle r="34" fill="none" stroke={INK} strokeWidth="5" />
          <rect x="-15" y="-15" width="30" height="30" fill={INK} />
        </>
      ),
    },
    {
      title: '切る',
      a: '元電源を遮断する',
      b: '空気・油圧も',
      icon: (
        <>
          <path d="M-6-34L-22 4H-4L-10 34 22-8H2L10-34Z" fill={INK} />
          <path d="M-30 30L30-30" stroke={ACC_D} strokeWidth="6" strokeLinecap="round" />
        </>
      ),
    },
    {
      title: '施錠する',
      a: '自分の鍵をかける',
      b: '札に名前を書く',
      icon: (
        <>
          <path d="M-16-8v-12a16 16 0 0 1 32 0v12" fill="none" stroke={INK} strokeWidth="6" />
          <rect x="-25" y="-8" width="50" height="40" rx="5" fill={INK} />
          <circle cx="0" cy="12" r="5" fill="#fff" />
        </>
      ),
    },
    {
      title: '確かめる',
      a: '起動しないことを',
      b: '試してから触る',
      icon: (
        <>
          <circle r="34" fill="none" stroke={INK} strokeWidth="5" />
          <path
            d="M-16 2l10 12 22-26"
            fill="none"
            stroke={ACC_D}
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ),
    },
  ];

  return (
    <svg viewBox="0 0 680 520" preserveAspectRatio="xMidYMid meet" role="img" aria-label="機械に手を入れる前の4手順（止める・切る・施錠する・確かめる）を示す図">
      {steps.map((s, i) => {
        const x = 7 + i * 172;
        return (
          <g key={s.title}>
            <rect x={x} y="24" width="150" height="400" rx="8" fill="#fff" stroke={INK} strokeWidth="2.6" />
            <circle cx={x + 75} cy="76" r="25" fill={ACC_D} />
            <text
              x={x + 75}
              y="87"
              fontFamily={DIAGRAM_FONT}
              fontSize="30"
              fontWeight="800"
              fill="#fff"
              textAnchor="middle"
            >
              {i + 1}
            </text>
            <g transform={`translate(${x + 75},194)`}>{s.icon}</g>
            <text
              x={x + 75}
              y="304"
              fontFamily={DIAGRAM_FONT}
              fontSize="31"
              fontWeight="800"
              fill={INK}
              textAnchor="middle"
            >
              {s.title}
            </text>
            <text x={x + 75} y="348" fontFamily={DIAGRAM_FONT} fontSize="17" fill={SUB} textAnchor="middle">
              {s.a}
            </text>
            <text x={x + 75} y="374" fontFamily={DIAGRAM_FONT} fontSize="17" fill={SUB} textAnchor="middle">
              {s.b}
            </text>
            {i < steps.length - 1 && <path d={`M${x + 155} 187l11 7-11 7z`} fill={ACC_D} />}
          </g>
        );
      })}
      <text
        x="340"
        y="482"
        fontFamily={DIAGRAM_FONT}
        fontSize="22"
        fontWeight="700"
        fill={INK}
        textAnchor="middle"
      >
        一つも飛ばさない。作業が終わるまで、鍵は自分が持つ。
      </text>
    </svg>
  );
};

/** 転倒: 三つの形（つまずく・滑る・踏み外す）を足元の図で示す。 */
const TripTypesDiagram: React.FC = () => {
  /* 側面から見た安全靴。つま先が右を向く。原点は踵の上端、底面は y=58。 */
  const BOOT = 'M0 0h20v40h18a10 10 0 0 1 10 10v8H0Z';
  const panels: Array<{ title: string; sub: string }> = [
    { title: 'つまずく', sub: '通路の物・コード' },
    { title: '滑る', sub: '床の水・油' },
    { title: '踏み外す', sub: '段差・階段の縁' },
  ];

  return (
    <svg viewBox="0 0 680 520" preserveAspectRatio="xMidYMid meet" role="img" aria-label="転倒の三類型（つまずく・滑る・踏み外す）を示す図">
      {panels.map((p, i) => {
        const cx = 113 + i * 227;
        return (
          <g key={p.title}>
            <path d={`M${cx - 92} 270H${cx + 92}`} stroke={INK} strokeWidth="4" />

            {i === 0 && (
              <>
                {/* つま先が障害物に当たる */}
                <g transform={`translate(${cx - 60},165) scale(1.8) rotate(-12 0 58)`} fill={INK}>
                  <path d={BOOT} />
                </g>
                <rect x={cx + 26} y="232" width="54" height="38" fill={SUB} />
                <path d={`M${cx - 6} 126q34 -24 62 10`} stroke={ACC_D} strokeWidth="4.5" fill="none" />
                <path d={`M${cx + 46} 126l16 14-20 6z`} fill={ACC_D} />
              </>
            )}

            {i === 1 && (
              <>
                {/* 濡れた床で足が前に滑る */}
                <g transform={`translate(${cx - 52},160) scale(1.8) rotate(-22 0 58)`} fill={INK}>
                  <path d={BOOT} />
                </g>
                <g stroke={ACC_D} strokeWidth="4.5" fill="none">
                  <path d={`M${cx + 6} 246h44`} />
                  <path d={`M${cx + 16} 262h44`} />
                </g>
                <path d={`M${cx + 52} 240l18 8-18 8z`} fill={ACC_D} />
                <g fill="#93c5fd">
                  <ellipse cx={cx - 62} cy="262" rx="20" ry="7" />
                  <ellipse cx={cx + 70} cy="264" rx="14" ry="5" />
                </g>
              </>
            )}

            {i === 2 && (
              <>
                {/* 段差の縁を踏み外す */}
                <path d={`M${cx - 92} 220H${cx - 10}V270`} stroke={INK} strokeWidth="4" fill="none" />
                <g transform={`translate(${cx - 44},118) scale(1.8) rotate(14 0 58)`} fill={INK}>
                  <path d={BOOT} />
                </g>
                <path d={`M${cx + 52} 176v56`} stroke={ACC_D} strokeWidth="4.5" />
                <path d={`M${cx + 42} 226h20l-10 18z`} fill={ACC_D} />
              </>
            )}

            <text
              x={cx}
              y="372"
              fontFamily={DIAGRAM_FONT}
              fontSize="36"
              fontWeight="800"
              fill={INK}
              textAnchor="middle"
            >
              {p.title}
            </text>
            <text x={cx} y="416" fontFamily={DIAGRAM_FONT} fontSize="23" fill={SUB} textAnchor="middle">
              {p.sub}
            </text>
          </g>
        );
      })}
      {/* 仕切り */}
      <g stroke="#e2e8f0" strokeWidth="2.5">
        <path d="M226 40V440" />
        <path d="M453 40V440" />
      </g>
      <text
        x="340"
        y="492"
        fontFamily={DIAGRAM_FONT}
        fontSize="21"
        fontWeight="700"
        fill={INK}
        textAnchor="middle"
      >
        どれも、床と通路を直せば防げる。
      </text>
    </svg>
  );
};

const DIAGRAMS: Record<string, React.FC> = {
  heatSymptoms: HeatSymptomsDiagram,
  fallHeight: FallHeightDiagram,
  caughtLoto: CaughtLotoDiagram,
  tripTypes: TripTypesDiagram,
};

// ──────────────────────────────────────────────────────────────
// QR ブロック
// ──────────────────────────────────────────────────────────────

/** 実トークン発行済み（ログイン済み）か、見本かを型で分ける。取り違えると死んだQRを掲示させてしまう。 */
export type PosterQr =
  | { kind: 'sample'; url: string }
  | { kind: 'live'; url: string; caption: string };

// ──────────────────────────────────────────────────────────────
// 紙面
// ──────────────────────────────────────────────────────────────

/** 緊急連絡先・担当者。どちらも任意で、空欄なら手書き用の罫線で刷る。
 *  現場ごと・当日ごとに変わる情報なので、印字を強制しない。 */
export interface PosterContact {
  tel?: string;
  person?: string;
}

export interface PosterSheetProps {
  theme: PosterTheme;
  variant: PosterVariant;
  /** 掲示者（公開フォーム入力）。 */
  orgName: string;
  siteName: string;
  contact?: PosterContact;
  qr: PosterQr;
  /** 一覧のミニチュア用。画像の遅延読み込みを有効にする。 */
  lazyImage?: boolean;
}

/** 掲示者バー。ブランドではなく会社名を最上部に置く。
 *  業者のチラシに見えると、現場の掲示板には貼られない。
 *  2系統の紙面で共通なので切り出す。 */
const OrgBar: React.FC<{ orgName: string; siteName: string }> = ({ orgName, siteName }) => (
  <div className="ap-org">
    <div className="ap-org-who">
      {/* 未入力なら手書き用の記入欄として刷る。ダミー社名を入れて刷らせない。 */}
      {orgName ? <span className="ap-org-name">{orgName}</span> : <span className="ap-org-blank" />}
      {siteName && <span className="ap-org-site">{siteName}</span>}
    </div>
    <div className="ap-org-tag">安 全 掲 示</div>
  </div>
);

/** 緊急連絡先・担当者の欄。入力があれば印字し、空欄なら現場で手書きさせる罫線で刷る。
 *  罫線を残すのは、現場ごと・当日ごとに変わる連絡先を1枚のPDFに焼き付けないため。 */
const ContactFields: React.FC<{ telLabel?: string; contact?: PosterContact }> = ({
  telLabel = '緊急連絡先',
  contact,
}) => (
  <div className="ap-contact">
    <div className="ap-field">
      <div className="ap-field-l">{telLabel}</div>
      <div className="ap-field-u ap-field-tel">
        {contact?.tel ? <span className="ap-field-v">{contact.tel}</span> : null}
      </div>
    </div>
    <div className="ap-field">
      <div className="ap-field-l">担当者</div>
      <div className="ap-field-u ap-field-nm">
        {contact?.person ? <span className="ap-field-v">{contact.person}</span> : null}
      </div>
    </div>
  </div>
);

/** QR本体。見本のときは紙面に「見本」と刻印し、死んだQRの掲示を防ぐ。 */
const QrBox: React.FC<{ qr: PosterQr; className: string }> = ({ qr, className }) => (
  <div className={className}>
    <QRCodeSVG value={qr.url} level="M" style={{ width: '100%', height: '100%' }} />
    {qr.kind === 'sample' && <div className="ap-qr-stamp">見本</div>}
  </div>
);

/** 読み取れないとき用のURL表記。見本のときは警告に差し替える。 */
const QrCaption: React.FC<{ qr: PosterQr; className: string }> = ({ qr, className }) =>
  qr.kind === 'live' ? (
    <div className={className}>{qr.caption}</div>
  ) : (
    <div className={`${className} ap-qr-warn`}>※ このQRは見本です。読み取っても報告はできません。</div>
  );

// ──────────────────────────────────────────────────────────────
// 災害別テーマの紙面（絵/図解 ＋ 症状・直後の対処・根拠法令）
// ──────────────────────────────────────────────────────────────

const HazardPosterSheet: React.FC<{
  theme: HazardPosterTheme;
  variant: HazardPosterVariant;
  orgName: string;
  siteName: string;
  contact?: PosterContact;
  qr: PosterQr;
  lazyImage?: boolean;
}> = ({ theme, variant, orgName, siteName, contact, qr, lazyImage }) => {
  const Diagram = variant.diagram ? DIAGRAMS[variant.diagram] : null;

  return (
    <div className="ap-sheet">
      <OrgBar orgName={orgName} siteName={siteName} />

      <div className="ap-hero">
        <div className="ap-slogan">
          <div className="ap-s1">{variant.s1}</div>
          <div className="ap-s2">
            <RichLines lines={variant.s2} />
          </div>
        </div>
        <div className={Diagram ? 'ap-pic ap-pic-diagram' : 'ap-pic'}>
          {Diagram ? (
            <Diagram />
          ) : (
            <img
              src={variant.img}
              alt=""
              loading={lazyImage ? 'lazy' : undefined}
              decoding="async"
              style={{ objectPosition: variant.pos }}
            />
          )}
        </div>
      </div>

      <div className="ap-symline">
        <div className="ap-symline-t">{theme.check.label}</div>
        <div className="ap-symline-v">{theme.check.value}</div>
      </div>

      {/* 緊急時は電話。QRは「倒れる前」の予兆を集めるもので、役割が違う。
          だから連絡先は記入欄にして、現場で必ず書かせる。 */}
      <div className="ap-emerg">
        <div className="ap-emerg-t">
          {theme.immediate.title} ── <span>{theme.immediate.accent}</span>
        </div>
        <div className="ap-emerg-s">{theme.immediate.desc}</div>
        <ContactFields contact={contact} />
      </div>

      <div className="ap-qr">
        <QrBox qr={qr} className="ap-qr-img" />
        <div className="ap-qr-body">
          <div className="ap-qr-h">
            <RichLines lines={theme.qr.h} />
          </div>
          <ul className="ap-qr-ex">
            {theme.qr.ex.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
          <div className="ap-qr-m">
            カメラをかざすだけ・約30秒。<b>名前は残りません。</b>
            <QrCaption qr={qr} className="ap-qr-u" />
          </div>
        </div>
      </div>

      {/* 根拠法令は小さく、ブランドは右下に最小で。 */}
      <div className="ap-foot">
        <div className="ap-legal">
          <Rich line={theme.legal} />
        </div>
        <PosterBrand />
      </div>
    </div>
  );
};

/** 右下のブランド。掲示物の主役ではないので最小サイズで固定。 */
const PosterBrand: React.FC = () => (
  <div className="ap-brand">
    <div className="ap-brand-n">安全ポスト＋</div>
    <div className="ap-brand-u">anzenpost.com</div>
  </div>
);

// ──────────────────────────────────────────────────────────────
// 汎用テーマの紙面（画像なし）
//
// docs/design-handoff/poster-prototype/general.html の移植。
// 災害別と違い、下段（症状／直後の対処／根拠法令）を持たない。
// 業種横断で使うため未確認の法令を引用せず、代わりにフッターで
// 「集めた情報をどう扱うか」を約束する。
// ──────────────────────────────────────────────────────────────

const GeneralPosterSheet: React.FC<{
  variant: GeneralPosterVariant;
  orgName: string;
  siteName: string;
  contact?: PosterContact;
  qr: PosterQr;
}> = ({ variant, orgName, siteName, contact, qr }) => {
  // 最小版。他の掲示物が並ぶ場所や、密度を上げたくない場所向け。
  // 紙面の大半を余白にし、QRだけを大きく見せる。
  if (variant.minimal) {
    return (
      <div className="ap-sheet">
        <OrgBar orgName={orgName} siteName={siteName} />

        <div className="ap-g-mini">
          <div className="ap-g-mini-lead">{variant.s1}</div>
          <div className="ap-g-mini-main">
            <RichLines lines={variant.s2} />
          </div>
          <QrBox qr={qr} className="ap-g-mini-qr" />
          <div className="ap-g-mini-note">
            スマホのカメラをかざすだけ・約30秒
            <br />
            <b>名前は残りません。</b>
          </div>
          <QrCaption qr={qr} className="ap-g-mini-url" />
        </div>

        <div className="ap-g-contact">
          <ContactFields telLabel="困ったときの連絡先" contact={contact} />
        </div>

        <div className="ap-foot ap-g-foot">
          <div className="ap-g-note">
            この掲示は、事故になる前の「気づき」を集めるためのものです。報告した方を探したり、責任を問うために使うことはありません。
          </div>
          <PosterBrand />
        </div>
      </div>
    );
  }

  return (
    <div className="ap-sheet">
      <OrgBar orgName={orgName} siteName={siteName} />

      {/* 見出し。画像が無いぶん文字で紙面を支える。 */}
      <div className="ap-g-hero">
        <div className="ap-g-k">{variant.s1}</div>
        <div className="ap-g-m">
          <RichLines lines={variant.s2} />
        </div>
        <div className="ap-g-rule" />
      </div>

      {/* 「何を報告していいか分からない」を潰す例示。あえて小さい例を並べる。 */}
      <div className="ap-g-exlabel">
        こんなことで、<span>かまいません</span>
      </div>
      <div className="ap-g-ex">
        {variant.ex.map((e) => (
          <div key={e}>{e}</div>
        ))}
      </div>

      {/* 「怒られるかも」を潰す約束。ここがこの紙のいちばんの主張。 */}
      <div className="ap-g-promise">
        <div className="ap-g-promise-t">
          まだ何も起きていないうちが、
          <br />
          <em>いちばん直しやすい。</em>
        </div>
        <ul>
          <li>名前は残りません。誰が報告したかは分かりません</li>
          <li>報告したことで不利益を受けることはありません</li>
          <li>直したい人を探すためではなく、直したい場所を見つけるために集めています</li>
        </ul>
      </div>

      <div className="ap-g-qr">
        <QrBox qr={qr} className="ap-g-qr-img" />
        <div className="ap-g-qr-body">
          <div className="ap-g-qr-h">
            スマホのカメラを
            <br />
            かざすだけ。
          </div>
          <div className="ap-g-qr-s">
            アプリは不要。文字でも、声でも、写真でも送れます。
            <br />
            <b>約30秒</b>で終わります。
          </div>
          <QrCaption qr={qr} className="ap-g-qr-u" />
        </div>
      </div>

      <div className="ap-g-contact">
        <ContactFields telLabel="困ったときの連絡先" contact={contact} />
      </div>

      <div className="ap-foot ap-g-foot">
        <div className="ap-g-note">
          この掲示は、事故になる前の「気づき」を集めるためのものです。集まった内容は、個人が特定できない形で整理し、職場の改善に使います。報告した方を探したり、責任を問うために使うことはありません。
        </div>
        <PosterBrand />
      </div>
    </div>
  );
};

/** テーマの系統に応じて紙面を出し分ける。
 *
 *  theme と variant は必ず同じ系統の組で渡ってくる（POSTER_THEMES がそう組まれている）が、
 *  2つの prop の対応を型で表現できないので、両方を確かめてから分岐する。
 *  どちらにも当てはまらない組み合わせは描かない（壊れた紙面を刷らせない）。 */
export const PosterSheet: React.FC<PosterSheetProps> = ({
  theme,
  variant,
  orgName,
  siteName,
  contact,
  qr,
  lazyImage,
}) => {
  if (theme.kind === 'general' && variant.kind === 'general') {
    return (
      <GeneralPosterSheet
        variant={variant}
        orgName={orgName}
        siteName={siteName}
        contact={contact}
        qr={qr}
      />
    );
  }
  if (theme.kind === 'hazard' && variant.kind !== 'general') {
    return (
      <HazardPosterSheet
        theme={theme}
        variant={variant}
        orgName={orgName}
        siteName={siteName}
        contact={contact}
        qr={qr}
        lazyImage={lazyImage}
      />
    );
  }
  return null;
};

/** 縮小表示のラッパ。画面では scale、印刷では等倍に戻す。 */
export const PosterScaler: React.FC<{ scale: number; children: React.ReactNode }> = ({
  scale,
  children,
}) => (
  <div className="ap-scaler" style={{ ['--ap-scale' as string]: String(scale) } as React.CSSProperties}>
    {children}
  </div>
);

/** 親要素の幅に収まる scale を返す。
 *
 * ref は useRef ではなく callback ref にしている。useRef だと、計測対象が
 * 後から現れる画面（一覧 → プレビューの切り替え）で effect が再実行されず、
 * 縮小率が初期値のまま張り付いて紙面が等倍で表示されてしまう。 */
export function useFitScale(maxScale = 1) {
  const [el, setEl] = React.useState<HTMLDivElement | null>(null);
  const [scale, setScale] = React.useState(maxScale);

  React.useEffect(() => {
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(maxScale, w / A4_WIDTH_PX));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, maxScale]);

  return { ref: setEl, scale };
}

/* 紙面のCSS。1度だけ差し込む。 */
export const PosterSheetStyles: React.FC = () => (
  <style>{`
.ap-scaler { width: calc(210mm * var(--ap-scale, 1)); height: calc(297mm * var(--ap-scale, 1)); }
.ap-scaler > .ap-sheet { transform: scale(var(--ap-scale, 1)); transform-origin: top left; }

.ap-sheet {
  width: 210mm; height: 297mm; flex: none;
  display: flex; flex-direction: column;
  background: #fff; color: ${INK}; overflow: hidden;
  font-family: "Hiragino Kaku Gothic ProN","Yu Gothic","Meiryo",system-ui,sans-serif;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
  font-feature-settings: "palt";
}
.ap-sheet * { box-sizing: border-box; margin: 0; padding: 0; }

.ap-org { flex: none; display: flex; align-items: baseline; justify-content: space-between; padding: 7mm 12mm 3.5mm; gap: 4mm; }
.ap-org-who { min-width: 0; }
.ap-org-name { font-size: 12pt; font-weight: 700; }
.ap-org-site { font-size: 10pt; color: ${SUB}; margin-left: 3mm; }
.ap-org-blank { display: inline-block; width: 62mm; height: 5mm; border-bottom: 1.2pt solid ${INK}; vertical-align: bottom; }
.ap-org-tag { flex: none; font-size: 8pt; letter-spacing: .3em; color: ${SUB}; }

.ap-hero { flex: 1; display: flex; flex-direction: column; min-height: 0; }
.ap-slogan { flex: none; padding: 0 12mm 4mm; }
.ap-s1 { font-size: 15.5pt; font-weight: 700; color: ${SUB}; }
.ap-s2 { font-size: 31pt; font-weight: 800; line-height: 1.2; margin-top: 1mm; letter-spacing: -.01em; }
.ap-s2 em { font-style: normal; color: ${ACC_D}; }
.ap-pic { flex: 1; min-height: 0; overflow: hidden; background: #fdf6ec; border-top: 1.4pt solid ${INK}; border-bottom: 1.4pt solid ${INK}; }
.ap-pic img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ap-pic-diagram { background: #fff; display: flex; align-items: center; justify-content: center; padding: 3mm 6mm; }
.ap-pic-diagram svg { width: 100%; height: 100%; }

.ap-symline { flex: none; display: flex; align-items: center; gap: 3mm; margin: 0 12mm; padding: 3mm 0; border-bottom: 1.2pt solid ${INK}; font-size: 9.5pt; line-height: 1.4; }
.ap-symline-t { flex: none; background: ${INK}; color: #fff; font-size: 9pt; font-weight: 700; padding: 1.2mm 2.5mm; white-space: nowrap; }
.ap-symline-v { font-weight: 700; }

.ap-emerg { flex: none; padding: 4.5mm 12mm 0; }
.ap-emerg-t { font-size: 12pt; font-weight: 800; }
.ap-emerg-t span { color: ${ACC_D}; }
.ap-emerg-s { font-size: 9pt; color: ${SUB}; margin-top: 1.2mm; line-height: 1.45; }
.ap-contact { margin-top: 3mm; display: flex; gap: 5mm; align-items: flex-end; }
.ap-field { display: flex; align-items: flex-end; gap: 2mm; }
.ap-field-l { font-size: 9.5pt; font-weight: 700; color: ${SUB}; white-space: nowrap; }
.ap-field-u { border-bottom: 1.6pt solid ${INK}; height: 7mm; display: flex; align-items: center; }
/* 入力があったときだけ印字する。枠幅は固定なので、あふれても紙面の桁は崩さない。 */
.ap-field-v { font-size: 12pt; font-weight: 700; line-height: 1; white-space: nowrap; overflow: hidden; }
.ap-field-tel { width: 58mm; }
.ap-field-nm { width: 38mm; }

.ap-qr { flex: none; display: flex; gap: 5.5mm; align-items: center; margin: 4.5mm 12mm 0; padding: 5mm; background: #fff7ed; border: 1.6pt solid ${ACC_D}; }
/* QRは実寸50mm前後を確保する（読み取り距離の目安がQR幅の約10倍）。 */
.ap-qr-img { position: relative; flex: none; width: 50mm; height: 50mm; padding: 2mm; background: #fff; border: 1.2pt solid ${INK}; }
.ap-qr-body { min-width: 0; }
.ap-qr-stamp {
  position: absolute; left: 50%; top: 50%; transform: translate(-50%,-50%) rotate(-14deg);
  font-size: 20pt; font-weight: 800; letter-spacing: .1em; color: ${ACC_D};
  background: rgba(255,255,255,.86); border: 1.6pt solid ${ACC_D}; padding: 1mm 3mm; white-space: nowrap;
}
.ap-qr-h { font-size: 19pt; font-weight: 800; line-height: 1.25; }
.ap-qr-h em { font-style: normal; color: ${ACC_D}; }
.ap-qr-ex { margin-top: 2.5mm; font-size: 10.5pt; line-height: 1.5; }
.ap-qr-ex li { list-style: none; }
.ap-qr-ex li::before { content: "「"; color: ${ACC_D}; font-weight: 700; }
.ap-qr-ex li::after { content: "」"; color: ${ACC_D}; font-weight: 700; }
.ap-qr-m { margin-top: 2.5mm; font-size: 9.5pt; color: ${SUB}; line-height: 1.5; }
.ap-qr-m b { color: ${INK}; }
.ap-qr-u { font-size: 8.5pt; }
.ap-qr-warn { color: ${ACC_D}; font-weight: 700; }

.ap-foot { flex: none; margin-top: auto; display: flex; align-items: flex-end; justify-content: space-between; gap: 6mm; padding: 4mm 12mm 7mm; }
.ap-legal { font-size: 7pt; line-height: 1.5; color: ${SUB}; max-width: 134mm; }
.ap-legal b { color: ${INK}; }
.ap-brand { text-align: right; white-space: nowrap; }
.ap-brand-n { font-size: 9.5pt; font-weight: 700; color: ${ACC_D}; }
.ap-brand-u { font-size: 7.5pt; color: ${SUB}; }

/* ── 汎用テーマ（画像なし）──
   general.html の寸法をそのまま移植する。画像帯が無いぶん、
   見出しの級数と余白で紙面を支えている（数値を丸めると崩れる）。 */
.ap-g-hero { flex: none; padding: 2mm 12mm 0; }
.ap-g-k { font-size: 14pt; font-weight: 700; color: ${SUB}; }
.ap-g-m { font-size: 44pt; font-weight: 800; line-height: 1.18; letter-spacing: -.025em; margin-top: 2mm; }
.ap-g-m em { font-style: normal; color: ${ACC_D}; }
.ap-g-rule { height: 2.6pt; width: 52mm; background: ${INK}; margin: 6mm 0 5mm; }

.ap-g-exlabel { flex: none; font-size: 12pt; font-weight: 800; padding: 0 12mm; }
.ap-g-exlabel span { color: ${ACC_D}; }
.ap-g-ex { flex: none; margin: 3.5mm 12mm 0; display: grid; grid-template-columns: 1fr 1fr; gap: 2.5mm; }
.ap-g-ex > div { border: 1.2pt solid ${LINE}; border-left: 3pt solid ${ACC}; padding: 3mm 3.5mm; font-size: 11.5pt; font-weight: 700; line-height: 1.45; }
.ap-g-ex > div::before { content: "「"; color: ${ACC_D}; }
.ap-g-ex > div::after { content: "」"; color: ${ACC_D}; }

.ap-g-promise { flex: none; margin: 6mm 12mm 0; padding: 5mm 6mm; background: ${INK}; color: #fff; }
.ap-g-promise-t { font-size: 17pt; font-weight: 800; line-height: 1.35; }
.ap-g-promise-t em { font-style: normal; color: ${ACC}; }
.ap-g-promise ul { margin-top: 3mm; font-size: 10.5pt; line-height: 1.75; }
.ap-g-promise li { list-style: none; padding-left: 5mm; position: relative; }
.ap-g-promise li::before { content: "✓"; position: absolute; left: 0; color: ${ACC}; font-weight: 800; }

.ap-g-qr { flex: none; display: flex; gap: 6mm; align-items: center; margin: 6mm 12mm 0; padding: 5mm; border: 1.6pt solid ${INK}; }
.ap-g-qr-img { position: relative; flex: none; width: 52mm; height: 52mm; padding: 2mm; background: #fff; border: 1pt solid ${LINE}; }
.ap-g-qr-body { min-width: 0; }
.ap-g-qr-h { font-size: 20pt; font-weight: 800; line-height: 1.3; }
.ap-g-qr-s { margin-top: 2.5mm; font-size: 11pt; line-height: 1.6; }
.ap-g-qr-u { margin-top: 2mm; font-size: 9pt; color: ${SUB}; }

/* 連絡先。法令根拠は載せないので、この記入欄が「相談先」の役割も兼ねる。 */
.ap-g-contact { flex: none; margin: 5mm 12mm 0; }
.ap-g-contact .ap-contact { margin-top: 0; }

.ap-g-foot { border-top: 0.8pt solid ${LINE}; }
.ap-g-note { font-size: 7.5pt; line-height: 1.6; color: ${SUB}; max-width: 132mm; }

/* 最小版。QRの実寸は 72mm − 内側余白3mm×2 ＝ 約65.5mm を確保する。 */
.ap-g-mini { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 0 16mm; }
.ap-g-mini-lead { font-size: 14pt; font-weight: 700; color: ${SUB}; }
.ap-g-mini-main { font-size: 32pt; font-weight: 800; line-height: 1.25; margin-top: 2.5mm; letter-spacing: -.02em; }
.ap-g-mini-main em { font-style: normal; color: ${ACC_D}; }
.ap-g-mini-qr { position: relative; width: 72mm; height: 72mm; margin: 10mm 0 6mm; padding: 3mm; border: 1.2pt solid ${INK}; }
.ap-g-mini-note { font-size: 12pt; line-height: 1.7; }
.ap-g-mini-url { font-size: 9pt; color: ${SUB}; margin-top: 3mm; }

/* ── 印刷 ──
   プレビュー画面は紙面を1枚しか描画していないので、@page と余白のリセットだけで
   「1ポスター = 1ページ」になる。画面用の scale は等倍に戻す。 */
@media print {
  @page { size: A4 portrait; margin: 0; }
  html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
  .no-print, .ap-no-print { display: none !important; }
  .ap-print-reset {
    display: block !important; margin: 0 !important; padding: 0 !important;
    background: #fff !important; box-shadow: none !important; border: 0 !important;
    max-width: none !important; width: auto !important; min-height: 0 !important;
    /* 管理画面のレイアウト（h-screen + overflow-hidden/auto）の中に紙面を置くと、
       これを戻さない限り画面1枚ぶんで切られて刷られる。 */
    height: auto !important; overflow: visible !important;
  }
  .ap-scaler { width: 210mm !important; height: 297mm !important; }
  .ap-scaler > .ap-sheet { transform: none !important; }
  .ap-sheet { box-shadow: none !important; break-inside: avoid; }
}
`}</style>
);
